import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import * as ciap from "gcip-iap";
import { useEffect, useRef, useState } from "react";
import {
  appUrlFor,
  isAllowedTenant,
  readTenantIdFromUrl,
} from "./shared/brand/tenants";
import { type FramedReport, isFramed, reportToParent } from "./shared/frame";

type Auth = firebase.auth.Auth;
type UserCredential = firebase.auth.UserCredential;
type Persistence = firebase.auth.Auth.Persistence;

type GcipProject = {
  apiKey: string;
  authDomain: string;
  persistence: Persistence;
};

// GCIP projects this page serves. One deployed page can front multiple
// environments' IAPs (stage + prod) because IAP forwards the *originating*
// project's apiKey on redirect; we map that apiKey to the matching project's
// authDomain here. This replaces reading the hosting project's
// /__/firebase/init.json, which only produced the right authDomain when the
// page was hosted in the same project as Identity Platform — it can't serve a
// project it isn't hosted in (the source of the earlier invalid-key error).
//
// These web API keys are PUBLIC identifiers (they ship in this client bundle
// regardless), so committing them adds no exposure. Access is controlled by the
// keys' HTTP-referrer restrictions + each project's GCIP authorized domains,
// not by secrecy. Override per-deploy via VITE_{STAGE,PROD}_{API_KEY,AUTH_DOMAIN}
// (e.g. on key rotation) without a code change; the committed values are the
// current stage/prod ones.
const PROJECTS: readonly GcipProject[] = [
  {
    apiKey:
      import.meta.env.VITE_STAGE_API_KEY ??
      "AIzaSyCSWsKZzKaCmgODsAmSDrMatJnHZ9QPY3c",
    authDomain:
      import.meta.env.VITE_STAGE_AUTH_DOMAIN ??
      "c1g-apps-stage.firebaseapp.com",
    persistence: firebase.auth.Auth.Persistence.SESSION,
  },
  {
    apiKey:
      import.meta.env.VITE_PROD_API_KEY ??
      "AIzaSyBJIaZKegkwtlXcJ3FMPCLJ1Ig_aId1rqo",
    authDomain:
      import.meta.env.VITE_PROD_AUTH_DOMAIN ?? "c1g-apps-prod.firebaseapp.com",
    // The SDK default, set explicitly. Production's session length is still
    // open (known-gaps N8), and prod gets the stage bundle unchanged
    // (`make promote`), so the choice is made per project, not per build.
    persistence: firebase.auth.Auth.Persistence.LOCAL,
  },
];

const AUTH_DOMAIN_BY_API_KEY: Readonly<Record<string, string>> =
  Object.fromEntries(PROJECTS.map((p) => [p.apiKey, p.authDomain]));

const PERSISTENCE_BY_API_KEY: Readonly<Record<string, Persistence>> =
  Object.fromEntries(PROJECTS.map((p) => [p.apiKey, p.persistence]));

// Resolve the authDomain for the project whose apiKey IAP forwarded (sign-in)
// or the reset link carried (email action). Fail loud on an unknown/absent key
// rather than initializing Firebase against the wrong (or no) project.
function authDomainFor(apiKey: string | null | undefined): string {
  // Own entries only: "constructor" would otherwise find Object.prototype's.
  const authDomain =
    apiKey && Object.hasOwn(AUTH_DOMAIN_BY_API_KEY, apiKey)
      ? AUTH_DOMAIN_BY_API_KEY[apiKey]
      : undefined;
  if (!authDomain) {
    throw new Error(
      `Unknown GCIP project for API key '${apiKey ?? "(none)"}'. Add its ` +
        `{apiKey, authDomain} to PROJECTS in auth.ts — this page only serves ` +
        `the configured stage/prod projects.`,
    );
  }
  return authDomain;
}

// The compat SDK keeps one default app per page. The first caller initializes
// it for the project the apiKey names; every later caller gets that app back.
function appFor(apiKey: string | null | undefined): firebase.app.App {
  try {
    return firebase.app();
  } catch {
    firebase.initializeApp({ apiKey, authDomain: authDomainFor(apiKey) });
    return firebase.app();
  }
}

// gcip-iap's own error type carries `message` without extending Error, and a
// rejection can be anything. Read the message where there is one.
function messageOf(err: unknown): string | null {
  const message = (err as { message?: unknown } | null | undefined)?.message;
  return typeof message === "string" && message ? message : null;
}

// Firebase and gcip-iap errors carry a string `code`; read it where there
// is one.
function codeOf(err: unknown): string | undefined {
  const code = (err as { code?: unknown } | null | undefined)?.code;
  return typeof code === "string" && code ? code : undefined;
}

// gcip-iap's refusal to run in a cross-origin iframe, verbatim from its
// dist/index.esm.js (SignInOperation.process). It raises this only after
// finding no usable stored sign-in: with one, it renews without looking at
// the frame. To the Lernwelt that refusal means "the learner has to sign
// in", not "the renewal broke". src/gcip-iap-refusal.test.ts holds this
// text and its code to the installed library, so an upgrade that changes
// either fails the suite, not the stage test.
export const CROSS_ORIGIN_IFRAME_REFUSAL =
  "The page is displayed in a cross origin iframe.";

// GCIP's answer to a token refresh for a disabled account. gcip-iap drops
// the stored user on it and asks for the form, without saying why;
// src/gcip-iap-account-disabled.test.ts holds that path to the installed
// library.
export const ACCOUNT_DISABLED_CODE = "auth/user-disabled";

function isCrossOriginIframeRefusal(err: unknown): boolean {
  return (
    codeOf(err) === "permission-denied" &&
    messageOf(err) === CROSS_ORIGIN_IFRAME_REFUSAL
  );
}

export type GcipAuthStatus = "loading" | "ready" | "error" | "signedOut";

type SignInResolver = {
  resolve: (credential: UserCredential) => void;
  reject: (reason: unknown) => void;
};

// useGcipAuth orchestrates the IAP <-> Identity Platform handshake via the
// gcip-iap library. The library calls back into `handler` at well-defined
// points; our job is to translate those into React state so the UI can render.
//
// Sign-in flow:
//   1. IAP redirects the user here with apiKey/tenantId in the URL.
//   2. ciap.Authentication(handler).start() parses those and calls getAuth().
//   3. With a user for that tenant still stored on this origin (the Firebase
//      session, see PROJECTS.persistence), gcip-iap mints a fresh ID token
//      from it and hands that to IAP at once: no selectTenant(), no
//      startSignIn(), nothing rendered but the loading card. This is how an
//      expired IAP cookie is renewed without a password. Only without a
//      stored user (or when IAP forces a re-authentication) does it call
//      selectTenant() and then startSignIn(). A stored user that GCIP refuses
//      as disabled or token-expired is dropped and startSignIn() follows.
//   4. startSignIn() returns a Promise that we keep open via resolverRef --
//      the React form fulfils it when sign-in succeeds.
//   5. gcip-iap then redirects back to IAP with the ID token; IAP issues a
//      session cookie and forwards the user to the originally-requested URL.
//
// Sign-out flow (IAP redirects here with ?mode=signout — no tenant id):
//   the same start() call routes by mode. gcip-iap signs the user out of the
//   stored GCIP tenant sessions, then either redirects back to IAP (`state`
//   present — the normal app logout) or calls completeSignOut() (no redirect
//   target, e.g. the URL was opened directly) — surfaced as 'signedOut'.
//
// Framed (IAP's session refresher, ADR-0020, src/shared/frame.ts): step 3
// is the whole point, and it needs nothing from this page. When gcip-iap
// has no stored sign-in to renew from, it either refuses the cross-origin
// iframe outright or, having dropped a refused user, asks for the form via
// startSignIn(). Neither can be answered in a hidden frame; both are
// reported to the embedding Lernwelt once, and the handshake ends. A stored
// user whose account is disabled is reported with its reason: gcip-iap
// discards GCIP's code, so processUser() refreshes the token itself first
// and keeps the code before the library sees the failure.
export function useGcipAuth() {
  const [status, setStatus] = useState<GcipAuthStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const authRef = useRef<Auth | null>(null);
  const resolverRef = useRef<SignInResolver | null>(null);

  useEffect(() => {
    let cancelled = false;

    // One report per page load, to the tenant's app origin. Without a known
    // tenant there is no origin to address, and nothing is sent: the message
    // never goes to "*".
    let reported = false;
    // Why the stored user was refused, when GCIP said so: read in
    // processUser(), reported by startSignIn(), which gcip-iap calls next.
    let refusal: FramedReport["reason"];
    function reportFramed(report: Omit<FramedReport, "source">) {
      if (reported) return;
      reported = true;
      const appUrl = appUrlFor(readTenantIdFromUrl());
      if (appUrl) {
        reportToParent({ source: "lernwelt-login", ...report }, appUrl);
      }
    }

    const handler: ciap.AuthenticationHandler = {
      getAuth(apiKey, tenantId) {
        const auth = appFor(apiKey).auth();
        if (tenantId !== auth.tenantId) {
          auth.tenantId = tenantId || null;
        }
        authRef.current = auth;
        return auth;
      },

      // Fail closed (ADR-0010): only tenants in config/tenants.json are
      // served. Prefer the tenant IAP put in the URL (tid); otherwise accept
      // exactly one allowlisted tenant from IAP's list. Anything else rejects
      // — no fallback to list[0] or the project-level IdP. We only advertise
      // password sign-in — no Google button.
      selectTenant(_projectConfig, tenantIds) {
        const list = tenantIds || [];
        const urlTenantId = readTenantIdFromUrl();
        if (
          urlTenantId &&
          list.includes(urlTenantId) &&
          isAllowedTenant(urlTenantId)
        ) {
          return Promise.resolve({
            tenantId: urlTenantId,
            providerIds: ["password"],
          });
        }
        const allowed = list.filter(isAllowedTenant);
        if (allowed.length === 1) {
          return Promise.resolve({
            tenantId: allowed[0],
            providerIds: ["password"],
          });
        }
        return Promise.reject(
          new Error(
            `Keiner der angebotenen Mandanten ist für diese Seite konfiguriert (erhalten: [${list.join(", ")}]).`,
          ),
        );
      },

      // gcip-iap calls this with the stored user right before it fetches the
      // token, and on auth/user-disabled or auth/user-token-expired drops the
      // user and calls startSignIn() without the code. Framed, the token is
      // fetched here first: a disabled account is noted, and the failure is
      // thrown on unchanged, so the library takes the same path as before.
      // A revoked token (user-token-expired) is not noted: signing in again
      // is the answer there, and the code does not say why it was revoked.
      // The full page needs none of this; its form says that the account is
      // disabled when the learner tries.
      processUser(user) {
        if (!isFramed()) {
          return Promise.resolve(user);
        }
        const withToken = user as {
          getIdToken(forceRefresh: boolean): Promise<string>;
        };
        return withToken.getIdToken(true).then(
          () => user,
          (err: unknown) => {
            if (codeOf(err) === ACCOUNT_DISABLED_CODE) {
              refusal = "account-disabled";
            }
            throw err;
          },
        );
      },

      startSignIn(auth) {
        // Framed: a form nobody can see or fill in, and the Lernwelt would
        // wait forever. Report that a sign-in is required and refuse; the
        // status never reaches "ready", so no form renders.
        if (isFramed()) {
          reportFramed({
            type: "sign-in-required",
            ...(refusal ? { reason: refusal } : {}),
          });
          return Promise.reject(
            new Error(
              "Anmeldung erforderlich: im eingebetteten Fenster wird kein Formular angezeigt.",
            ),
          );
        }
        return new Promise<UserCredential>((resolve, reject) => {
          authRef.current = auth;
          resolverRef.current = { resolve, reject };
          if (!cancelled) setStatus("ready");
        });
      },

      // Reached only when the signout redirect has no `state` to return to;
      // with `state`, gcip-iap navigates back to IAP before this is called.
      completeSignOut() {
        if (!cancelled) setStatus("signedOut");
        return Promise.resolve();
      },
    };

    (async () => {
      try {
        // The typings declare start() as void; the library returns the
        // Promise of the whole operation (dist/index.esm.js,
        // Authentication.prototype.start), and its rejection is the only
        // signal that the handshake failed.
        await (new ciap.Authentication(
          handler,
        ).start() as unknown as Promise<void>);
      } catch (err) {
        console.error(err);
        if (isFramed()) {
          reportFramed(
            isCrossOriginIframeRefusal(err)
              ? { type: "sign-in-required" }
              : {
                  type: "session-refresh-failed",
                  code: codeOf(err),
                  message: messageOf(err) ?? undefined,
                },
          );
        }
        if (!cancelled) {
          setError(messageOf(err) ?? "Sign-in failed.");
          setStatus("error");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  async function signIn(
    email: string,
    password: string,
  ): Promise<UserCredential> {
    const auth = authRef.current;
    if (!auth) throw new Error("Auth nicht bereit");
    // Stage keeps the Firebase session for this tab only (known-gaps I2).
    // Under `local` the refresh token survives in IndexedDB, and every expired
    // IAP cookie is renewed silently: nobody sees this page again. The IAP
    // cookie on the app domain is separate and not limited by this (N8).
    // getAuth() already failed on an unknown apiKey, so the lookup is set.
    // The compat typings leave the app options untyped; ours always carry
    // the apiKey appFor() initialized the app with.
    const { apiKey } = auth.app.options as { apiKey: string };
    if (!Object.hasOwn(PERSISTENCE_BY_API_KEY, apiKey)) {
      throw new Error(`No persistence configured for API key '${apiKey}'.`);
    }
    await auth.setPersistence(PERSISTENCE_BY_API_KEY[apiKey]);
    const result = await auth.signInWithEmailAndPassword(email, password);
    // Hand the credential back to gcip-iap; the library now redirects to IAP
    // with the ID token. After that redirect this component unmounts.
    resolverRef.current?.resolve(result);
    return result;
  }

  // Triggers a password-reset email via Firebase Auth. The tenantId on the
  // auth instance (set by getAuth above) is preserved, so the email goes out
  // for the right user pool. The reset link in the email points at the
  // project's configured action URL (configured in Identity Platform Console).
  async function sendPasswordReset(email: string): Promise<void> {
    const auth = authRef.current;
    if (!auth) throw new Error("Auth nicht bereit");
    await auth.sendPasswordResetEmail(email);
  }

  return { status, error, signIn, sendPasswordReset };
}

// useActionAuth is a *standalone* Firebase initialization for the email-action
// handler page (password reset, email verification, ...). It does NOT use
// gcip-iap because the user lands here by clicking a link in an email, not via
// an IAP redirect.
//
// The link from sendPasswordResetEmail() has the form:
//   <action-url>?mode=resetPassword&oobCode=...&apiKey=...&lang=...&tenantId=...
//
// We honor apiKey and tenantId from the URL so the action runs against the
// same project + user pool that issued the link: apiKey selects the project
// (-> authDomain via authDomainFor), tenantId the user pool. Both fail closed
// (ADR-0010): a missing/unknown apiKey throws in authDomainFor, and the
// tenantId must be present AND allowlisted — no fallback pool, no guessing.
// App.tsx guards the same via brand resolution, but the ?brand= override can
// bypass that; this check does not rely on it.
export function useActionAuth({
  apiKey,
  tenantId,
}: {
  apiKey?: string;
  tenantId?: string;
}) {
  const [auth, setAuth] = useState<Auth | null>(null);
  const [error, setError] = useState<unknown>(null);

  // Synchronous on purpose: initializing the compat app and binding the
  // tenant await nothing, so there is no late result to guard against.
  useEffect(() => {
    try {
      if (!tenantId || !isAllowedTenant(tenantId)) {
        throw new Error(
          tenantId
            ? `Der Mandant '${tenantId}' ist für diese Seite nicht konfiguriert.`
            : "Der Link enthält keinen Mandanten (tenantId). Bitte fordere einen neuen Link an.",
        );
      }
      const a = appFor(apiKey).auth();
      if (a.tenantId !== tenantId) {
        a.tenantId = tenantId;
      }
      setAuth(a);
    } catch (err) {
      console.error(err);
      setError(err);
    }
  }, [apiKey, tenantId]);

  return { auth, error };
}

// Map Firebase Auth error codes to user-visible German strings. Anything we
// don't recognize falls through to the raw message so we don't lose info.
export function humanizeAuthError(err: unknown): string {
  switch (codeOf(err)) {
    case "auth/invalid-email":
    case "auth/missing-email":
      return "Bitte gib eine gültige E-Mail-Adresse ein.";
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
    case "auth/invalid-login-credentials":
      return "E-Mail oder Passwort sind nicht korrekt.";
    case "auth/too-many-requests":
      return "Zu viele Versuche. Bitte später erneut versuchen.";
    case "auth/user-disabled":
      return "Dieser Account wurde deaktiviert.";
    case "auth/network-request-failed":
      return "Netzwerkfehler. Verbindung prüfen und erneut versuchen.";
    case "auth/expired-action-code":
      return "Der Link ist abgelaufen. Bitte fordere einen neuen an.";
    case "auth/invalid-action-code":
      return "Der Link ist ungültig oder wurde bereits verwendet.";
    case "auth/weak-password":
      return "Das Passwort erfüllt nicht alle Anforderungen.";
    default:
      return messageOf(err) ?? "Anmeldung fehlgeschlagen.";
  }
}
